import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Award, Globe, Headphones, Battery, Cable, Smartphone, Watch, Speaker } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1756705533779-105bf34e0722?w=1920&q=80';
const HEADPHONES_IMG = 'https://images.unsplash.com/photo-1578345218746-50a229b3d0f8?w=600&q=80';

export function AccessoriesPage() {
  const { i18n } = useTranslation();

  const categories = [
    {
      number: '01',
      icon: Headphones,
      title: i18n.language === 'fr' ? 'Audio' : i18n.language === 'ar' ? 'صوتيات' : 'Audio',
      desc: i18n.language === 'fr' ? 'Écouteurs, casques, enceintes Bluetooth' : i18n.language === 'ar' ? 'سماعات أذن، سماعات رأس، مكبرات صوت بلوتوث' : 'Earphones, headphones, Bluetooth speakers'
    },
    {
      number: '02',
      icon: Battery,
      title: i18n.language === 'fr' ? 'Énergie' : i18n.language === 'ar' ? 'طاقة' : 'Power',
      desc: i18n.language === 'fr' ? 'Chargeurs, batteries externes, câbles' : i18n.language === 'ar' ? 'شواحن، بطاريات خارجية، كابلات' : 'Chargers, power banks, cables'
    },
    {
      number: '03',
      icon: Shield,
      title: i18n.language === 'fr' ? 'Protection' : i18n.language === 'ar' ? 'حماية' : 'Protection',
      desc: i18n.language === 'fr' ? 'Coques, films, étuis premium' : i18n.language === 'ar' ? 'أغطية، أفلام حماية، حافظات فاخرة' : 'Cases, screen protectors, premium pouches'
    },
    {
      number: '04',
      icon: Watch,
      title: i18n.language === 'fr' ? 'Wearables' : i18n.language === 'ar' ? 'أجهزة قابلة للارتداء' : 'Wearables',
      desc: i18n.language === 'fr' ? 'Montres connectées, bracelets fitness' : i18n.language === 'ar' ? 'ساعات ذكية، أساور لياقة' : 'Smart watches, fitness bands'
    },
    {
      number: '05',
      icon: Cable,
      title: i18n.language === 'fr' ? 'Connectique' : i18n.language === 'ar' ? 'توصيل' : 'Connectivity',
      desc: i18n.language === 'fr' ? 'Adaptateurs, hubs, stations d\'accueil' : i18n.language === 'ar' ? 'محولات، موزعات، محطات إرساء' : 'Adapters, hubs, docking stations'
    },
    {
      number: '06',
      icon: Smartphone,
      title: i18n.language === 'fr' ? 'Support' : i18n.language === 'ar' ? 'حوامل' : 'Mounts',
      desc: i18n.language === 'fr' ? 'Supports voiture, bureau, selfie sticks' : i18n.language === 'ar' ? 'حوامل سيارة، مكتب، عصي سيلفي' : 'Car mounts, desk stands, selfie sticks'
    }
  ];

  const advantages = [
    {
      icon: Award,
      title: i18n.language === 'fr' ? 'Qualité Premium' : i18n.language === 'ar' ? 'جودة فاخرة' : 'Premium Quality',
      desc: i18n.language === 'fr' ? 'Sélection rigoureuse des fournisseurs et contrôle qualité systématique.' : i18n.language === 'ar' ? 'اختيار دقيق للموردين ومراقبة جودة منهجية.' : 'Rigorous supplier selection and systematic quality control.'
    },
    {
      icon: Globe,
      title: i18n.language === 'fr' ? 'Sourcing Global' : i18n.language === 'ar' ? 'توريد عالمي' : 'Global Sourcing',
      desc: i18n.language === 'fr' ? 'Réseau international de partenaires pour les meilleurs produits.' : i18n.language === 'ar' ? 'شبكة دولية من الشركاء لأفضل المنتجات.' : 'International network of partners for the best products.'
    },
    {
      icon: Shield,
      title: i18n.language === 'fr' ? 'Conformité' : i18n.language === 'ar' ? 'الامتثال' : 'Compliance',
      desc: i18n.language === 'fr' ? 'Produits certifiés CE, conformes aux normes européennes.' : i18n.language === 'ar' ? 'منتجات معتمدة CE، متوافقة مع المعايير الأوروبية.' : 'CE certified products, compliant with European standards.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div data-testid="accessories-page">
      {/* Hero Section with background */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="accessories-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Accessories" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        <div className="container-main relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-label text-gold mb-6">
              {i18n.language === 'fr' ? 'ACCESSOIRES MOBILES' : i18n.language === 'ar' ? 'إكسسوارات الهواتف' : 'MOBILE ACCESSORIES'}
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8">
              {i18n.language === 'fr' ? (
                <>
                  Accessoires<br/>
                  <span className="text-gold">premium</span>
                </>
              ) : i18n.language === 'ar' ? (
                <>
                  إكسسوارات<br/>
                  <span className="text-gold">فاخرة</span>
                </>
              ) : (
                <>
                  Premium<br/>
                  <span className="text-gold">accessories</span>
                </>
              )}
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              {i18n.language === 'fr'
                ? 'Sourcing et distribution d\'accessoires mobiles de qualité pour opérateurs télécom et retailers.'
                : i18n.language === 'ar'
                ? 'توريد وتوزيع إكسسوارات الهواتف عالية الجودة لمشغلي الاتصالات وتجار التجزئة.'
                : 'Sourcing and distribution of quality mobile accessories for telecom operators and retailers.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-dark section-xl" data-testid="categories-section">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-16"
          >
            <motion.p variants={itemVariants} className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'NOS CATÉGORIES' : i18n.language === 'ar' ? 'فئاتنا' : 'OUR CATEGORIES'}
            </motion.p>
            <motion.h2 variants={itemVariants} className="text-display text-white">
              {i18n.language === 'fr' ? 'Gamme complète' : i18n.language === 'ar' ? 'مجموعة كاملة' : 'Complete range'}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {categories.map((cat) => (
              <motion.div 
                key={cat.number} 
                variants={itemVariants}
                className="card-numbered-dark group"
              >
                <span className="card-number">{cat.number}</span>
                <div className="flex items-center gap-3 mb-3">
                  <cat.icon className="w-5 h-5 text-gold" />
                  <h3 className="text-heading text-white">{cat.title}</h3>
                </div>
                <p className="text-muted text-sm">{cat.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="section-cream section-xl" data-testid="advantages-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'POURQUOI NOUS' : i18n.language === 'ar' ? 'لماذا نحن' : 'WHY US'}
            </p>
            <h2 className="text-display">
              {i18n.language === 'fr' ? (
                <>Nos <span className="text-gold">avantages</span></>
              ) : i18n.language === 'ar' ? (
                <><span className="text-gold">مزايانا</span></>
              ) : (
                <>Our <span className="text-gold">advantages</span></>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-3 gap-8"
          >
            {advantages.map((adv, index) => (
              <motion.div 
                key={adv.title}
                variants={itemVariants}
                className="card-simple text-center"
              >
                <div className="icon-circle mx-auto mb-6">
                  <adv.icon className="w-6 h-6" />
                </div>
                <h3 className="text-heading mb-3">{adv.title}</h3>
                <p className="text-muted">{adv.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark section-lg" data-testid="accessories-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-display text-white mb-6">
              {i18n.language === 'fr' ? (
                <>Besoin d'un <span className="text-gold">catalogue</span> ?</>
              ) : i18n.language === 'ar' ? (
                <>هل تحتاج <span className="text-gold">كتالوج</span> ؟</>
              ) : (
                <>Need a <span className="text-gold">catalog</span>?</>
              )}
            </h2>
            <p className="text-white/60 text-body-lg max-w-xl mx-auto mb-10">
              {i18n.language === 'fr'
                ? 'Contactez-nous pour recevoir notre catalogue complet et nos conditions.'
                : i18n.language === 'ar'
                ? 'اتصل بنا للحصول على كتالوجنا الكامل وشروطنا.'
                : 'Contact us to receive our complete catalog and terms.'}
            </p>
            <Link to="/contact" className="btn-gold">
              {i18n.language === 'fr' ? 'Demander le catalogue' : i18n.language === 'ar' ? 'اطلب الكتالوج' : 'Request catalog'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default AccessoriesPage;
